import { useEffect, useState } from "react";
import { api, errText } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Loading } from "./common";
import { DollarSign, Star, Gamepad2, Trophy, Save } from "lucide-react";
import { toast } from "sonner";

// Admin economic controls: VirtuStars+ pricing (grandfathered for existing members),
// FiveM script price, and the global VirtuCoin prize auto-granted on award wins.
export function PricingAdmin() {
  const [cfg, setCfg] = useState(null);
  const [f, setF] = useState({ monthly: "", yearly: "", fivem_price: "", award_auto_vc: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/config").then(({ data }) => {
      setCfg(data);
      setF({
        monthly: String(data?.pricing?.monthly ?? 14.99),
        yearly: String(data?.pricing?.yearly ?? 149),
        fivem_price: String(data?.fivem_price ?? 19.99),
        award_auto_vc: String(data?.award_auto_vc ?? 0),
      });
    }).catch((e) => toast.error(errText(e)));
  }, []);

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        pricing: { monthly: Math.max(0, parseFloat(f.monthly) || 0), yearly: Math.max(0, parseFloat(f.yearly) || 0) },
        fivem_price: Math.max(0, parseFloat(f.fivem_price) || 0),
        award_auto_vc: Math.max(0, parseFloat(f.award_auto_vc) || 0),
      };
      const { data } = await api.put("/admin/config", payload);
      setCfg(data);
      toast.success("Pricing updated");
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  if (!cfg) return <Loading />;

  const Field = ({ label, k, prefix, suffix, hint, icon: Icon }) => (
    <div className="surface rounded-2xl p-5" data-testid={`pricing-card-${k}`}>
      <div className="flex items-center gap-2 text-sm font-semibold mb-1"><Icon size={16} className="text-gold" />{label}</div>
      {hint && <p className="text-xs text-muted-foreground mb-3">{hint}</p>}
      <div className="flex items-center gap-2 surface-2 border border-white/10 rounded-xl px-3 h-11 max-w-[220px]">
        {prefix && <span className="text-muted-foreground text-sm">{prefix}</span>}
        <input type="number" step="0.01" min="0" value={f[k]} onChange={(e) => set(k, e.target.value)}
          className="bg-transparent flex-1 outline-none text-lg font-mono w-full" data-testid={`pricing-input-${k}`} />
        {suffix && <span className="text-muted-foreground text-xs">{suffix}</span>}
      </div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="pricing-admin">
      <div className="surface rounded-2xl p-4 flex items-center gap-2 text-sm text-muted-foreground">
        <DollarSign size={16} className="text-gold shrink-0" />
        New prices apply to <b className="text-white mx-1">new</b> subscribers & buyers only — existing VirtuStars+ members keep their locked-in rate (grandfathered).
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="VirtuStars+ — Monthly" k="monthly" prefix="$" suffix="/mo" icon={Star}
          hint="Charged monthly for new subscribers." />
        <Field label="VirtuStars+ — Yearly" k="yearly" prefix="$" suffix="/yr" icon={Star}
          hint="Charged yearly for new subscribers." />
        <Field label="FiveM Script" k="fivem_price" prefix="$" suffix="one-time" icon={Gamepad2}
          hint="One-time server license price shown on the FiveM page." />
        <Field label="Award Win — Auto VirtuCoins" k="award_auto_vc" suffix="VC" icon={Trophy}
          hint="VirtuCoins automatically awarded to each winner when their category is revealed. Set 0 to disable. Change any year." />
      </div>

      <Button onClick={save} disabled={busy} className="btn-gold rounded-xl h-11 px-6" data-testid="pricing-save-btn">
        <Save size={16} className="mr-1.5" />{busy ? "Saving…" : "Save pricing"}
      </Button>
    </div>
  );
}
